import * as React from "react";
import { Globe, MapPin, Plane, PlaneLanding, PlaneTakeoff } from "lucide-react";

import { DetailField } from "@/components/requests/DetailCard";
import { TripLike, tripTypeLabel } from "@/lib/trip";

function Cell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

/** The trip-location cells of a detail dialog's two-column grid, for a travel
 *  request or an advance: the trip type, then the locations that type uses -
 *  country, start city and end city for Domestic; starting and destination
 *  country for International. A document raised before the Domestic/
 *  International choice existed has no locations beyond its country, so it
 *  shows the single "Destination" cell it always did. */
export function TripRows({ trip }: { trip: TripLike }) {
  const typeLabel = tripTypeLabel(trip);
  if (trip.travelType === "international") {
    return (
      <>
        <Cell label="Trip type" value={typeLabel} />
        <Cell label="Starting country" value={trip.originCountry || "-"} />
        <Cell label="Final destination country" value={trip.country} />
      </>
    );
  }
  if (trip.travelType === "domestic") {
    return (
      <>
        <Cell label="Trip type" value={typeLabel} />
        <Cell label="Country" value={trip.country} />
        <Cell label="Start city" value={trip.startCity || "-"} />
        <Cell label="End city" value={trip.endCity || "-"} />
      </>
    );
  }
  return <Cell label="Destination" value={trip.country} />;
}

/** Same trip-location cells as TripRows, but as icon-prefixed DetailFields for
 *  the card-grouped detail dialogs (RequestDetailDialog / AdvanceDetailDialog).
 *  Trip type, then the locations that type uses. */
export function TripDetailFields({ trip }: { trip: TripLike }) {
  const typeLabel = tripTypeLabel(trip);
  if (trip.travelType === "international") {
    return (
      <>
        <DetailField icon={Plane} label="Trip type" value={typeLabel} />
        <DetailField
          icon={PlaneTakeoff}
          label="Starting country"
          value={trip.originCountry || "-"}
        />
        <DetailField
          icon={PlaneLanding}
          label="Final destination country"
          value={trip.country}
        />
      </>
    );
  }
  if (trip.travelType === "domestic") {
    return (
      <>
        <DetailField icon={Plane} label="Trip type" value={typeLabel} />
        <DetailField icon={Globe} label="Country" value={trip.country} />
        <DetailField
          icon={MapPin}
          label="Start city"
          value={trip.startCity || "-"}
        />
        <DetailField
          icon={MapPin}
          label="End city"
          value={trip.endCity || "-"}
        />
      </>
    );
  }
  return <DetailField icon={Globe} label="Destination" value={trip.country} />;
}
